import { memo, useCallback, useMemo } from 'react';

import { Button, CopyText, Flex, Heading, Label, Text, TruncatedText } from '@/components/core';
import { HowToGuide } from '@/components/flow';
import { WarningDuotoneIcon } from '@/components/icons';
import { N8NLogo } from '@/components/logos';
import { toActionId, toAppClientId, useGetAction, useGetAppClient, useListAppClients } from '@/hooks/api';
import { useTriggerStore } from '@/store';
import { isNotConfiguredExport } from '@/utils';

export type ExportN8NConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const IExportN8NConfiguration = ({}: ExportN8NConfigurationProps = {}) => {
  const action = useTriggerStore((state) => state.action);
  const update = useTriggerStore((state) => state.updateAction);

  const { data: appClients } = useListAppClients();
  const appClientWithSecret = appClients?.data?.find((appClient) => appClient.hasSecret);

  const { data: appClient } = useGetAppClient(toAppClientId(appClientWithSecret?.appClientId), {
    refetchInterval: (query) => (!query.state.data?.clientSecret ? 2000 : undefined),
  });

  const { data: exportAction } = useGetAction(toActionId(action.actionId), {
    refetchInterval: (query) =>
      !query.state.data?.config?.url || !query.state.data?.config?.nodeId ? 2000 : undefined,
  });

  const isConfigured = useMemo(() => !isNotConfiguredExport(exportAction), [exportAction]);

  const onDisconnect = useCallback(() => {
    update({ config: { ...action.config, url: null, nodeId: null } });
  }, [action.config, update]);

  const onSetupClicked = useCallback(() => {
    window.open('https://n8n.io', '_blank');
  }, []);

  return (
    <Flex col className="gap-md p-md grow">
      <Flex col className="gap-sm">
        <div className="gap-sm flex items-center">
          <Text size="xs" className="font-semibold text-gray-600">
            Account
          </Text>

          <div className="grow" />

          {isConfigured ? (
            <>
              <div className="border-success-100 bg-success-500 size-3 rounded-full border-3" />
              <Text size="xs" className="text-success-700 font-semibold">
                Connected
              </Text>
            </>
          ) : (
            <>
              <div className="size-3 rounded-full border-3 border-gray-100 bg-gray-500" />
              <Text size="xs" className="font-semibold text-gray-500">
                Not connected
              </Text>
            </>
          )}
        </div>

        <Flex className="p-sm gap-sm bg-gray-25 items-center rounded-md border-1 border-gray-100">
          <N8NLogo className="shrink-0" size="xl" />

          <TruncatedText className="grow-0">
            {isConfigured
              ? ((exportAction?.config?.workflowName ?? exportAction?.config.nodeId) as string | undefined)
              : 'Sign in to connect'}
          </TruncatedText>

          <div className="grow" />

          {isConfigured ? (
            <Button className="shrink-0" variant="secondary" onPress={onDisconnect}>
              <Text>Disconnect</Text>
            </Button>
          ) : (
            <Button className="shrink-0" variant="primary" onPress={onSetupClicked}>
              <Text>Sign in</Text>
            </Button>
          )}
        </Flex>
      </Flex>

      {!isConfigured ? (
        <>
          <Flex variant="plain" col className="p-md gap-4 rounded-lg border-1 border-gray-100 bg-white shadow-xs">
            <Flex col className="gap-xs">
              <Label>Client ID</Label>
              <CopyText value={appClient?.clientId} />
            </Flex>

            <Flex col className="gap-xs">
              <Label>Client Secret</Label>
              <CopyText isSensitive value={appClient?.clientSecret} />
            </Flex>
          </Flex>

          <Flex variant="warning" className="p-md gap-sm rounded-lg">
            <WarningDuotoneIcon />

            <Flex col className="border-none">
              <Heading size="sm">Setup required</Heading>
              <Text>
                Make sure your n8n node is configured and <strong>published</strong>.
              </Text>
            </Flex>
          </Flex>
        </>
      ) : null}

      <div className="grow" />

      <HowToGuide actionId={action.actionId} />
    </Flex>
  );
};

export const ExportN8NConfiguration = memo(IExportN8NConfiguration) as typeof IExportN8NConfiguration;
